import { useEffect, useState } from "react"
import { type LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import {
  LibraryNodeEmpty,
  LibraryNodeError,
  LibraryNodeLoading,
  type LibrarySlot,
} from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import { formatMs } from "@/lib/format-ms"
import { frameFormatClasses } from "@/lib/frame-format"
import {
  fetchLibraryShotAssets,
  type LibraryShotAssetsScene,
  type LibraryShotAssetsScope,
  type LibraryShotFolderShot,
} from "@/lib/library-api"
import {
  animaticSegmentSource,
  shotAudioAssetSource,
  shotFrameCaptureSource,
  shotReferenceInputSource,
  shotShootingBoardSource,
  shotStoryboardSource,
  shotVersionFrameSource,
  shotVersionMediaSource,
} from "@/lib/library-source"
import type { LibraryViewMode } from "@/lib/library-view-mode"
import type { ShotFrameCaptureRow } from "@/lib/shot-frame-captures-api"
import type { ShotFrameSource, ShotMediaVersionFrameRow } from "@/lib/workspace-api"

type Props = {
  projectId: number
  /** Шот — подписи как есть. Сцена и эпизод — те же плитки, с префиксом номера шота. */
  scope: LibraryShotAssetsScope
  frameFormat: string
  mode: LibraryViewMode
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  /** Плоский список содержимого — страница ведёт по нему клавиатуру и просмотрщик. */
  onItemsChange?: (items: LibraryItem[]) => void
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const NO_ITEMS: LibraryItem[] = []

/**
 * Плоский список ассетов шота: раскадровка, версии, скриншоты, звук, аниматики и референсы.
 * Сцена и эпизод — тот же список по всем нижележащим шотам. Пустых версий не бывает.
 */
export function LibraryShotFolder({
  projectId,
  scope,
  frameFormat,
  mode,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
}: Props) {
  const { t } = useTranslation(["workspace", "common"])
  // i18next-t не принимает произвольный ключ: прямой проброс в сборщик роняет tsc на перегрузках.
  const translate: Translate = (key, options) => (t as unknown as Translate)(key, options)
  const [slot, setSlot] = useState<LibrarySlot<LibraryShotAssetsScene[]>>({ status: "loading" })
  const [reloadKey, setReloadKey] = useState(0)
  const ff = frameFormatClasses(frameFormat)
  const shotId = "shotId" in scope ? scope.shotId : null
  const sceneId = "sceneId" in scope ? scope.sceneId : null
  const episodeId = "episodeId" in scope ? scope.episodeId : null

  useEffect(() => {
    if (shotId == null && sceneId == null && episodeId == null) return
    // Дерево меняет выбранную папку на каждое нажатие стрелки, поэтому запрос отменяем.
    const controller = new AbortController()
    const requestScope: LibraryShotAssetsScope =
      shotId != null ? { shotId } : sceneId != null ? { sceneId } : { episodeId: episodeId as number }
    setSlot({ status: "loading" })
    fetchLibraryShotAssets(projectId, requestScope, { signal: controller.signal })
      .then((scenes) => setSlot({ status: "ready", data: scenes }))
      .catch(() => {
        if (!controller.signal.aborted) setSlot({ status: "error" })
      })
    return () => controller.abort()
  }, [projectId, shotId, sceneId, episodeId, reloadKey])

  if (slot.status === "loading" || slot.status === "error") {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {slot.status === "loading" ? (
          <LibraryNodeLoading />
        ) : (
          <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} />
        )}
      </>
    )
  }

  const scenes = slot.data
  const prefixLabels = shotId == null
  const items = scenes.flatMap((scene) =>
    scene.shots.flatMap((row) =>
      shotFolderItems(
        row.id,
        row.shot,
        row.versions ?? [],
        row.captures ?? [],
        translate,
        prefixLabels ? t("library.tree.shotLabel", { number: `${scene.number}.${row.number}` }) : null,
      ),
    ),
  )

  return (
    <>
      <LibraryItemsReporter items={items} onItemsChange={onItemsChange} />
      {items.length === 0 ? <LibraryNodeEmpty>{emptyMessage(scope, scenes, translate)}</LibraryNodeEmpty> : null}
      {items.length > 0 ? (
        <LibraryItems
          items={items}
          mode={mode}
          listLayout="table"
          aspectClass={ff.aspect}
          focusedKey={focusedKey}
          onFocusItem={onFocusItem}
          onOpenItem={onOpenItem}
        />
      ) : null}
    </>
  )
}

function emptyMessage(scope: LibraryShotAssetsScope, scenes: LibraryShotAssetsScene[], t: Translate): string {
  if ("episodeId" in scope && scenes.length === 0) return t("library.folders.noScenes")
  if ("sceneId" in scope && scenes.every((scene) => scene.shots.length === 0)) return t("library.folders.noShots")
  if ("shotId" in scope) return t("library.shot.empty")
  return t("library.folders.empty")
}

function scopeItem(item: LibraryItem, shotId: number, labelPrefix: string | null): LibraryItem {
  if (!labelPrefix) return item
  const label = `${labelPrefix} - ${item.label}`
  return {
    ...item,
    key: `sh${shotId}-${item.key}`,
    label,
    asset: item.asset ? { ...item.asset, title: `${labelPrefix} - ${item.asset.title}` } : undefined,
  }
}

function shotFolderItems(
  shotId: number,
  shot: LibraryShotFolderShot,
  versions: ShotMediaVersionFrameRow[],
  captures: ShotFrameCaptureRow[],
  t: Translate,
  labelPrefix: string | null,
): LibraryItem[] {
  const imageItem = (
    key: string,
    label: string,
    url: string | null,
    thumbUrl: string | null,
    createdAt?: string | null,
    source?: ShotFrameSource,
    current?: boolean,
  ): LibraryItem => ({
    key,
    kind: "image",
    label,
    thumbUrl: thumbUrl ?? url,
    createdAt,
    source,
    current,
    asset: url
      ? {
          title: label,
          kind: "image",
          url,
          downloadUrl: url,
          createdAt,
        }
      : undefined,
  })

  const captureItem = (capture: ShotFrameCaptureRow, versionName?: string, current?: boolean): LibraryItem => {
    const base = t("library.shot.capture")
    const label = versionName ? `${versionName} - ${base}` : base
    const timecode = formatMs(capture.timecode_ms)
    const title = timecode ? `${label} · ${timecode}` : label
    return {
      key: `capture-${capture.id}`,
      kind: "image",
      label,
      meta: timecode,
      createdAt: capture.created_at,
      thumbUrl: capture.image_thumb_url ?? capture.image_url,
      status: capture.status,
      errorTitle: capture.error_message,
      source: shotFrameCaptureSource(capture.id),
      current,
      asset:
        capture.status === "ready" && capture.image_url
          ? {
              title,
              kind: "image",
              url: capture.image_url,
              downloadUrl: capture.image_download_url ?? capture.image_url,
              createdAt: capture.created_at,
            }
          : undefined,
    }
  }

  const storyboardUrl = shot.storyboard_preview_url?.trim() || null
  const shootingBoardUrl = shot.shooting_board_preview_url?.trim() || null

  const storyboardItem = storyboardUrl
    ? imageItem(
        "storyboard",
        t("library.shot.storyboard"),
        storyboardUrl,
        storyboardUrl,
        shot.storyboard_created_at,
        shot.storyboard_attached === false ? undefined : shotStoryboardSource(shotId),
      )
    : null

  const shootingBoardItem = shootingBoardUrl
    ? imageItem(
        "shooting-board",
        t("library.shot.shootingBoard"),
        shootingBoardUrl,
        shootingBoardUrl,
        shot.shooting_board_created_at,
        shot.shooting_board_attached === false ? undefined : shotShootingBoardSource(shotId),
      )
    : null

  const versionIds = new Set(versions.map((v) => v.id))
  const capturesByVersion = new Map<number, ShotFrameCaptureRow[]>()
  const orphanCaptures: ShotFrameCaptureRow[] = []
  for (const capture of captures) {
    if (capture.version_id != null && versionIds.has(capture.version_id)) {
      const list = capturesByVersion.get(capture.version_id) ?? []
      list.push(capture)
      capturesByVersion.set(capture.version_id, list)
    } else {
      orphanCaptures.push(capture)
    }
  }

  const versionItems = (version: ShotMediaVersionFrameRow): LibraryItem[] => {
    const items: LibraryItem[] = []
    const versionName = version.name?.trim() || t("library.shot.versionFallback")
    const prefixed = (assetLabel: string) => `${versionName} - ${assetLabel}`
    const current = version.active
    const videoUrl = version.stream_url ?? version.download_url ?? version.preview_url ?? null
    if (videoUrl) {
      const label = prefixed(t("library.shot.video"))
      items.push({
        key: `v${version.id}-video`,
        kind: "video",
        label,
        createdAt: version.created_at,
        thumbUrl: version.preview_thumb_url ?? version.first_frame_thumb_url ?? null,
        source: shotVersionMediaSource(version.id),
        current,
        asset: {
          title: label,
          kind: "video",
          videoUrl,
          downloadUrl: version.download_url ?? null,
          createdAt: version.created_at,
          durationMs: version.duration_ms ?? null,
          frameRate: version.frame_rate ?? null,
          aspectRatio: version.timeline_frame_aspect_ratio ?? null,
          timelineFrameUrls: version.timeline_frame_urls ?? [],
          sourceId: version.id,
        },
      })
    }
    if (version.first_frame_url) {
      items.push(
        imageItem(
          `v${version.id}-first`,
          prefixed(t("frameLibrary.firstFrame")),
          version.first_frame_url,
          version.first_frame_thumb_url ?? version.first_frame_url,
          version.created_at,
          shotVersionFrameSource(version.id, "first"),
          current,
        ),
      )
    }
    if (version.last_frame_url) {
      items.push(
        imageItem(
          `v${version.id}-last`,
          prefixed(t("frameLibrary.lastFrame")),
          version.last_frame_url,
          version.last_frame_thumb_url ?? version.last_frame_url,
          version.created_at,
          shotVersionFrameSource(version.id, "last"),
          current,
        ),
      )
    }
    for (const capture of capturesByVersion.get(version.id) ?? []) {
      items.push(captureItem(capture, versionName, current))
    }
    return items
  }

  const referenceRows = [
    ...(shot.shot_reference_inputs?.storyboard ?? []),
    ...(shot.shot_reference_inputs?.video ?? []),
    ...(shot.shot_reference_inputs?.audio ?? []),
  ].filter((row) => row.url)

  const referenceItems: LibraryItem[] = referenceRows.map((row) => {
    const kind = row.media_type === "video" ? "video" : row.media_type === "audio" ? "audio" : "image"
    return {
      key: `ref-${row.id}`,
      kind,
      label: row.label,
      createdAt: row.created_at,
      thumbUrl: kind === "image" ? (row.thumb_url ?? row.url) : null,
      source: shotReferenceInputSource(row.id),
      asset: {
        title: row.label,
        kind,
        url: kind === "video" ? null : row.url,
        videoUrl: kind === "video" ? row.url : null,
        downloadUrl: row.download_url ?? row.url,
        createdAt: row.created_at,
      },
    }
  })
  if (storyboardItem) referenceItems.unshift(storyboardItem)

  const sortedVersions = [...versions].sort((a, b) => b.id - a.id)
  const orphanItems = orphanCaptures.map((capture) => captureItem(capture))

  const audioItems: LibraryItem[] = (shot.audio_assets ?? []).map((row) => {
    const label = row.name?.trim() || t("library.shot.audioFallback")
    return {
      key: `audio-${row.id}`,
      kind: "audio" as const,
      label,
      meta: row.voice_name?.trim() || null,
      createdAt: row.created_at,
      thumbUrl: null,
      aspectClass: "aspect-[5/2]",
      source: shotAudioAssetSource(row.id),
      asset: {
        title: label,
        kind: "audio" as const,
        url: row.url,
        downloadUrl: row.url,
        createdAt: row.created_at,
        durationMs: row.duration_ms,
      },
    }
  })

  const animaticItems: LibraryItem[] = (shot.animatic_sources ?? []).map((row) => {
    const label = row.original_filename?.trim() || t("library.shot.animaticFallback")
    return {
      key: `animatic-${row.id}`,
      kind: "video" as const,
      pickMedia: "shot_animatic" as const,
      label,
      meta: row.linked ? t("library.shot.animaticLinked") : t("library.shot.animaticDetached"),
      createdAt: row.created_at,
      thumbUrl: row.poster_url,
      source: row.segment_id != null ? animaticSegmentSource(row.segment_id) : undefined,
      asset: row.video_stream_url
        ? {
            title: label,
            kind: "video" as const,
            videoUrl: row.video_stream_url,
            createdAt: row.created_at,
            durationMs: row.duration_ms,
            frameRate: row.frame_rate,
            aspectRatio: row.aspect_ratio,
            sourceId: row.id,
          }
        : undefined,
    }
  })

  const items = [
    ...(shootingBoardItem ? [shootingBoardItem] : []),
    ...sortedVersions.flatMap((version) => versionItems(version)),
    ...orphanItems,
    ...audioItems,
    ...animaticItems,
    ...referenceItems,
  ]
  return items.map((item) => scopeItem(item, shotId, labelPrefix))
}
